import type { BoxProps, FlexProps } from '@chakra-ui/react';
import { Box, Flex, Text, useToken } from '@chakra-ui/react';
import { useTranslations } from '@ui/i18n';
import numbro from 'numbro';
import { Suspense } from 'react';

import { DynamicHighchartsReact } from './DynamicHighchartsReact';

export type AllocationPieChartProps = {
  data: {
    name: string;
    y: number;
    color: string;
  }[];
} & FlexProps;
export const AllocationPieChart = ({
  data,
  ...props
}: AllocationPieChartProps) => {
  const chartOptions = useChartOptions({
    data,
  });

  return (
    <Suspense>
      <Flex {...props}>
        <LegendList data={data} flex="1" />
        <Box position="relative">
          <DynamicHighchartsReact options={chartOptions} />
          {/* disallow interact with pie chart */}
          <Box position="absolute" top="0" left="0" bottom="0" right="0" />
        </Box>
      </Flex>
    </Suspense>
  );
};

const useChartOptions = ({
  data,
}: {
  data: {
    name: string;
    y: number;
    color: string;
  }[];
}): Highcharts.Options => {
  const [bgDisabledColor] = useToken('colors', ['bg-disabled']);
  const sum = data.reduce((sum, item) => sum + item.y, 0);
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      width:
        typeof window === 'undefined'
          ? 170
          : Math.min(window.innerWidth * 0.5, 170),
      height:
        typeof window === 'undefined'
          ? 170
          : Math.min(window.innerWidth * 0.5, 170),
      animation: false,
      backgroundColor: 'transparent',
      spacing: [0, 0, 0, 0],
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        size: '100%',
        dataLabels: {
          enabled: false,
        },
      },
      series: {
        allowPointSelect: false,
        pointPlacement: 'on',
      },
    },
    series: [
      {
        type: 'pie',
        colorByPoint: true,
        data: sum
          ? data
          : [
              {
                y: 99999999,
                color: bgDisabledColor,
              },
              {
                y: 0.1,
                color: bgDisabledColor,
              },
            ],
      },
    ],
  };
  return options;
};

const LegendList = ({
  data,
  ...props
}: {
  data: {
    name: string;
    y: number;
    color: string;
  }[];
} & BoxProps) => {
  const t = useTranslations();
  const sum = data.reduce((sum, item) => sum + item.y, 0);
  return (
    <Box {...props}>
      <Text mb="16px">{t('Allocation')}:</Text>
      {data.map((item, i) => {
        return (
          <Flex
            key={i}
            mb="10px"
            gap="6px"
            align="center"
            textStyle="bodySmall"
          >
            <Box
              w="10px"
              h="10px"
              rounded="99px"
              backgroundColor={item.color}
            />
            <Text>{`${item.name} ${numbro(sum ? item.y / sum : 0).format({
              output: 'percent',
              mantissa: 1,
            })}`}</Text>
          </Flex>
        );
      })}
    </Box>
  );
};
